import React, { useState } from 'react';
import { Switch } from '@/shared/components/ui/Switch';
import { Checkbox } from '@/shared/components/ui/Checkbox';
import { FormSection } from '@/shared/components/ui/FormSection';
import { useNotificationPreferences } from '../hooks/useNotificationPreferences';
import { getSupportedChannelsForType, type NotificationChannel, NotificationType } from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import { isTypeCheckedForChannel, toggleTypeForChannel } from '../utils/notificationTypeSelection';
import { useAccountSaveButton } from './account-save-context';
interface NotificationCategory {
  category: string;
  types: {
    type: NotificationType;
    label: string;
    description: string;
  }[];
}

/**
 * Notification categories and types
 * IMPORTANT: All types MUST exist in the NotificationType enum
 * Validate types at compile-time to prevent runtime errors
 */
const notificationCategories: NotificationCategory[] = [
  {
    category: 'Sistema',
    types: [
      {
        type: NotificationType.SYSTEM_MAINTENANCE,
        label: 'Manutenção do Sistema',
        description: 'Notificações sobre manutenções programadas'
      },
      {
        type: NotificationType.SYSTEM_UPDATE,
        label: 'Atualizações do Sistema',
        description: 'Novos recursos e melhorias disponíveis'
      },
      {
        type: NotificationType.SYSTEM_ALERT,
        label: 'Alertas do Sistema',
        description: 'Alertas importantes sobre o sistema'
      },
    ]
  },
  {
    category: 'Segurança',
    types: [
      {
        type: NotificationType.AUTH_SUCCESS,
        label: 'Login Bem-sucedido',
        description: 'Notificações de login em sua conta'
      },
      {
        type: NotificationType.PASSWORD_RESET,
        label: 'Redefinição de Senha',
        description: 'Quando sua senha for redefinida'
      },
      {
        type: NotificationType.TWO_FACTOR_ENABLED,
        label: 'Autenticação de Dois Fatores',
        description: 'Quando 2FA for habilitado/desabilitado'
      },
    ]
  },
  {
    category: 'Tickets',
    types: [
      {
        type: NotificationType.TICKET_ASSIGNED,
        label: 'Ticket Atribuído',
        description: 'Quando um ticket for atribuído a você'
      },
      {
        type: NotificationType.TICKET_STATUS_CHANGED,
        label: 'Status do Ticket Alterado',
        description: 'Mudanças no status dos tickets'
      },
      {
        type: NotificationType.TICKET_COMMENT_ADDED,
        label: 'Novo Comentário no Ticket',
        description: 'Comentários adicionados aos tickets'
      },
      {
        type: NotificationType.SLA_WARNING,
        label: 'SLA perto do vencimento',
        description: 'Aviso antes de um prazo de SLA vencer'
      },
      {
        type: NotificationType.SLA_BREACH,
        label: 'SLA violado',
        description: 'Quando um prazo de SLA é ultrapassado'
      },
    ]
  },
  {
    category: 'Conversas',
    types: [
      {
        type: NotificationType.CONVERSATION_MESSAGE_RECEIVED,
        label: 'Nova Mensagem',
        description: 'Novas mensagens em conversas'
      },
      {
        type: NotificationType.CONVERSATION_ASSIGNED,
        label: 'Conversa Atribuída',
        description: 'Quando uma conversa for atribuída a você'
      },
    ]
  },
  {
    category: 'Leads',
    types: [
      {
        type: 'lead_assigned' as NotificationType,
        label: 'Lead Atribuído',
        description: 'Quando um lead for atribuído a você'
      },
      {
        type: 'lead_status_changed' as NotificationType,
        label: 'Status do Lead Alterado',
        description: 'Mudanças no status dos leads'
      },
      {
        type: 'lead_activity_assigned' as NotificationType,
        label: 'Atividade de Lead Atribuída',
        description: 'Quando uma atividade de lead for atribuída a você'
      },
      {
        type: 'lead_alert_warning' as NotificationType,
        label: 'Lead em Alerta',
        description: 'Quando um lead seu entra em alerta por inatividade'
      },
      {
        type: 'lead_alert_critical' as NotificationType,
        label: 'Lead Crítico',
        description: 'Quando um lead entra em estado crítico de inatividade (responsável e gestor)'
      },
      {
        type: 'lead_weekly_digest' as NotificationType,
        label: 'Resumo Semanal de Leads Parados',
        description: 'Resumo semanal dos leads parados, enviado ao gestor do funil'
      },
    ]
  },
  {
    category: 'Checklists',
    types: [
      {
        type: 'checklist_assigned' as NotificationType,
        label: 'Checklist atribuído',
        description: 'Quando um checklist for atribuído a você'
      },
      {
        type: 'checklist_due_soon' as NotificationType,
        label: 'Checklist perto de expirar',
        description: 'Quando o prazo de um checklist seu estiver se aproximando'
      },
      {
        type: 'checklist_expired' as NotificationType,
        label: 'Checklist expirado',
        description: 'Quando um checklist seu expirar sem ser enviado'
      },
      {
        type: 'checklist_completed' as NotificationType,
        label: 'Checklist enviado para revisão',
        description: 'Quando um checklist for preenchido e enviado para revisão'
      },
      {
        type: 'checklist_approved' as NotificationType,
        label: 'Checklist aprovado',
        description: 'Quando um checklist seu for aprovado pelo revisor'
      },
      {
        type: 'checklist_rejected' as NotificationType,
        label: 'Checklist reprovado',
        description: 'Quando um checklist seu for reprovado pelo revisor'
      },
    ]
  },
  {
    category: 'Projetos',
    types: [
      {
        type: NotificationType.PROJECT_TASK_UNBLOCKED,
        label: 'Dependência liberada',
        description: 'Quando uma tarefa de projeto for desbloqueada por dependências'
      },
      {
        type: NotificationType.PROJECT_TASK_COMPLETED,
        label: 'Tarefa de projeto concluída',
        description: 'Quando uma tarefa de projeto for concluída'
      },
      {
        type: NotificationType.PROJECT_COMPLETED,
        label: 'Projeto concluído',
        description: 'Quando um projeto for concluído'
      },
      {
        type: NotificationType.PROJECT_TASK_OVERDUE,
        label: 'Tarefa de projeto atrasada',
        description: 'Quando uma tarefa de projeto ficar atrasada'
      },
    ]
  },
  {
    category: 'Pagamentos',
    types: [
      {
        type: NotificationType.PAYMENT_RECEIVED,
        label: 'Pagamento Recebido',
        description: 'Notificações de pagamentos confirmados'
      },
      {
        type: NotificationType.PAYMENT_FAILED,
        label: 'Falha no Pagamento',
        description: 'Alertas de pagamentos que falharam'
      },
      {
        type: NotificationType.SUBSCRIPTION_RENEWED,
        label: 'Assinatura Renovada',
        description: 'Quando uma assinatura for renovada'
      },
      {
        type: NotificationType.SUBSCRIPTION_EXPIRED,
        label: 'Assinatura Expirada',
        description: 'Quando uma assinatura expirar'
      },
      {
        type: NotificationType.CREDIT_USAGE_ALERT,
        label: 'Créditos Acabando',
        description: 'Ao usar 70%, 80%, 90% e 100% dos créditos, e nos alertas cadastrados em Cobrança'
      },
    ]
  },
  {
    category: 'Automação',
    types: [
      {
        type: NotificationType.WORKFLOW_AUTO_PAUSED,
        label: 'Workflow pausado automaticamente',
        description: 'Quando um workflow for pausado sozinho (10 falhas seguidas ou canal excluído)'
      },
      {
        type: NotificationType.WORKFLOW_FREQUENT_FAILURES,
        label: 'Workflow falhando com frequência',
        description: 'Quando um workflow ativo falhar em várias das últimas execuções, mesmo sem chegar a ser pausado (no máximo 1 aviso por dia)'
      },
      {
        type: NotificationType.CAMPAIGN_AUTO_PAUSED,
        label: 'Campanha pausada automaticamente',
        description: 'Quando uma campanha for pausada sozinha porque o canal dela saiu do ar'
      },
    ]
  },
  {
    category: 'Equipe',
    types: [
      {
        type: NotificationType.TEAM_MEMBER_ADDED,
        label: 'Membro Adicionado',
        description: 'Quando um membro entrar na equipe'
      },
      {
        type: NotificationType.TEAM_MEMBER_REMOVED,
        label: 'Membro Removido',
        description: 'Quando um membro sair da equipe'
      },
      {
        type: NotificationType.SHIFT_REMINDER,
        label: 'Lembrete de Turno',
        description: 'Lembretes sobre seus turnos de trabalho'
      },
    ]
  },
];

// Compile-time validation: ensure all types are valid NotificationType values
void notificationCategories.flatMap(c => c.types.map(t => t.type));

interface ChannelCardProps {
  channel: NotificationChannel;
  label: string;
  icon: React.ReactNode;
  enabled: boolean;
  onToggle: () => void;
}

/**
 * Channel toggle card with icon, label, and switch
 */
const ChannelCard: React.FC<ChannelCardProps> = ({
  channel,
  label,
  icon,
  enabled,
  onToggle,
}) => {
  return (
    <div
      className="rounded-lg border p-4"
      data-testid={`notifications-tab-channel-card-${channel}`}
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{
              backgroundColor: enabled ? 'var(--theme-primary-background)' : 'var(--theme-button-ghost-hover)',
              color: enabled ? 'var(--theme-primary)' : 'var(--theme-muted-text)',
            }}
          >
            {icon}
          </div>
          <div>
            <h4 className="font-medium" style={{ color: 'var(--theme-card-text)' }}>
              {label}
            </h4>
            <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
              {enabled ? 'Habilitado' : 'Desabilitado'}
            </p>
          </div>
        </div>

        <div data-testid={`notifications-tab-channel-toggle-${channel}`}>
          <Switch
            checked={enabled}
            onChange={() => onToggle()}
            size="sm"
          />
        </div>
      </div>
    </div>
  );
};

interface NotificationCheckboxCellProps {
  isEnabled: boolean;
  isChannelEnabled: boolean;
  onToggle: () => void;
}

/**
 * Checkbox cell for the notification type table
 */
const NotificationCheckboxCell: React.FC<NotificationCheckboxCellProps> = ({
  isEnabled,
  isChannelEnabled,
  onToggle,
}) => {
  return (
    <div className="flex items-center justify-center">
      <Checkbox
        checked={isEnabled}
        onChange={() => onToggle()}
        disabled={!isChannelEnabled}
      />
    </div>
  );
};

/**
 * NotificationsTab
 * Configure notification preferences per channel and type
 */
export const NotificationsTab: React.FC = () => {
  const { preferences, updatePreferences, isSaving } = useNotificationPreferences();
  const [localPreferences, setLocalPreferences] = useState(preferences);

  // Update local state when preferences change (already sanitized in hook)
  React.useEffect(() => {
    setLocalPreferences(preferences);
  }, [preferences]);

  const handleToggleChannel = (channel: NotificationChannel) => {
    setLocalPreferences({
      ...localPreferences,
      [channel]: {
        ...localPreferences[channel],
        enabled: !localPreferences[channel].enabled,
      },
    });
  };

  // Regra única (2026-08-17): marcado = recebe, não marcado = não recebe. A
  // regra vive em `notificationTypeSelection` e espelha o `determineChannels`
  // do backend — sem exceção por canal.
  const isTypeEnabled = (channel: NotificationChannel, type: NotificationType): boolean =>
    isTypeCheckedForChannel(localPreferences[channel].types, type);

  const handleToggleType = (channel: NotificationChannel, type: NotificationType) => {
    // Guard against null/undefined types
    if (!type) {
      return;
    }

    const newTypes = toggleTypeForChannel(localPreferences[channel].types, type);

    setLocalPreferences({
      ...localPreferences,
      [channel]: {
        ...localPreferences[channel],
        // Filter out any null/undefined that might have slipped in
        types: newTypes.filter((t): t is NotificationType => t != null),
      },
    });
  };

  const handleSave = () => {
    updatePreferences(localPreferences);
  };

  // Publish the primary save action to the page header (single primary button)
  useAccountSaveButton({
    onSave: handleSave,
    saving: isSaving,
    label: 'Salvar preferências',
    loadingLabel: 'Salvando...',
  });

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-8">
      <div className="space-y-6">
        {/* Section Header */}
        <FormSection
          icon={'bell'}
          title="Preferências de Notificação"
          description="Configure como você deseja receber notificações por cada canal"
        >
          {/* Channel Toggles */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            <ChannelCard
              channel="inApp"
              label="In-App (Plataforma)"
              icon={<Icon name="smartphone" className="w-5 h-5" />}
              enabled={localPreferences.inApp.enabled}
              onToggle={() => handleToggleChannel('inApp')}
            />
            <ChannelCard
              channel="push"
              label="Push (Navegador)"
              icon={<Icon name="bell" className="w-5 h-5" />}
              enabled={localPreferences.push.enabled}
              onToggle={() => handleToggleChannel('push')}
            />
            <ChannelCard
              channel="sound"
              label="Som"
              icon={<Icon name="music" className="w-5 h-5" />}
              enabled={localPreferences.sound.enabled}
              onToggle={() => handleToggleChannel('sound')}
            />
            <ChannelCard
              channel="whatsapp"
              label="WhatsApp"
              icon={<Icon name="message-square" className="w-5 h-5" />}
              enabled={localPreferences.whatsapp.enabled}
              onToggle={() => handleToggleChannel('whatsapp')}
            />
            <ChannelCard
              channel="email"
              label="Email"
              icon={<Icon name="mail" className="w-5 h-5" />}
              enabled={localPreferences.email.enabled}
              onToggle={() => handleToggleChannel('email')}
            />
          </div>

        </FormSection>

        {/* Notifications Table */}
        <div
          className="overflow-x-auto rounded-lg border"
          style={{
            backgroundColor: 'var(--theme-card-background)',
            borderColor: 'var(--theme-card-border)',
          }}
        >
          <table className="w-full">
            <thead>
              <tr
                className="border-b"
                style={{
                  backgroundColor: 'var(--theme-table-header-background)',
                  borderColor: 'var(--theme-card-border)',
                }}
              >
                <th
                  className="px-4 py-3 text-left text-sm font-medium"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  Tipo de Notificação
                </th>
                <th
                  className="px-4 py-3 text-center text-sm font-medium"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  In-App
                </th>
                <th
                  className="px-4 py-3 text-center text-sm font-medium"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  Push
                </th>
                <th
                  className="px-4 py-3 text-center text-sm font-medium"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  Som
                </th>
                <th
                  className="px-4 py-3 text-center text-sm font-medium"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  WhatsApp
                </th>
                <th
                  className="px-4 py-3 text-center text-sm font-medium"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  Email
                </th>
              </tr>
            </thead>
            <tbody>
              {notificationCategories.map((category) => (
                <React.Fragment key={category.category}>
                  {/* Category Header Row */}
                  <tr
                    className="border-b"
                    style={{
                      backgroundColor: 'var(--theme-table-header-background)',
                      borderColor: 'var(--theme-card-border)',
                    }}
                  >
                    <td
                      colSpan={6}
                      className="px-4 py-2 text-sm font-semibold"
                      style={{ color: 'var(--theme-card-text)' }}
                    >
                      {category.category}
                    </td>
                  </tr>

                  {/* Type Rows */}
                  {category.types.map((type) => {
                    const supported = new Set<NotificationChannel>(getSupportedChannelsForType(type.type));
                    const renderCell = (channel: NotificationChannel) => (
                      <td className="px-4 py-3">
                        {supported.has(channel) && (
                          <NotificationCheckboxCell
                            isEnabled={isTypeEnabled(channel, type.type)}
                            isChannelEnabled={localPreferences[channel].enabled}
                            onToggle={() => handleToggleType(channel, type.type)}
                          />
                        )}
                      </td>
                    );
                    return (
                      <tr
                        key={`${category.category}-${type.type}`}
                        className="border-b transition-colors"
                        style={{ borderColor: 'var(--theme-card-border)' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                              {type.label}
                            </p>
                            <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                              {type.description}
                            </p>
                          </div>
                        </td>
                        {renderCell('inApp')}
                        {renderCell('push')}
                        {renderCell('sound')}
                        {renderCell('whatsapp')}
                        {renderCell('email')}
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
